import { useState } from 'react';
import { Mail, Copy, X, ChevronDown, ChevronUp } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PlayerFormModal } from '@/components/director/player-form-modal';
import { useDirectorPlayers, useDeletePlayer } from '@/hooks/use-director-data';
import { useDebounce } from '@/hooks/use-debounce';
import { useNavigate } from 'react-router-dom';
import type { PlayerSummary } from '@/hooks/use-director-data';
import { PlayerTable } from '@/components/director/players/player-table';
import { PlayerFilters } from '@/components/director/players/player-filters';

export default function DirectorPlayersPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [ballLevelFilter, setBallLevelFilter] = useState<string>('');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<PlayerSummary | null>(null);
  const [deleteConfirmPlayer, setDeleteConfirmPlayer] = useState<PlayerSummary | null>(null);

  const [invitePanelOpen, setInvitePanelOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const debouncedSearch = useDebounce(searchInput, 300);
  const { data, isLoading } = useDirectorPlayers(debouncedSearch, levelFilter, ballLevelFilter);
  const deletePlayer = useDeletePlayer();

  const pendingInvites = useQuery(api.director.onboardingInvites.listPending);
  const sendInvite = useMutation(api.director.onboardingInvites.send);
  const revokeInvite = useMutation(api.director.onboardingInvites.revoke);

  const appUrl = window.location.origin;

  async function handleSendInvite() {
    if (!inviteEmail.trim()) return;
    setInviteSending(true);
    try {
      const result = await sendInvite({ parentEmail: inviteEmail.trim() });
      setInviteSent(result.code);
      setInviteEmail('');
    } finally {
      setInviteSending(false);
    }
  }

  function copyOnboardLink(code: string) {
    navigator.clipboard.writeText(`${appUrl}/parent/onboard?code=${code}`);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  }

  const handleAddPlayer = () => {
    setEditingPlayer(null);
    setFormModalOpen(true);
  };

  const handleEditPlayer = (player: PlayerSummary) => {
    setEditingPlayer(player);
    setFormModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmPlayer) {
      await deletePlayer.mutateAsync(deleteConfirmPlayer.id);
      setDeleteConfirmPlayer(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Players" subtitle="Manage all players in your club" />

      {/* Invite a Parent */}
      <div className="border border-border rounded-lg bg-muted/40">
        <button
          className="w-full flex items-center justify-between px-4 py-3 text-left"
          onClick={() => { setInvitePanelOpen(o => !o); setInviteSent(null); }}
        >
          <span className="flex items-center gap-2 font-medium text-foreground text-sm">
            <Mail className="w-4 h-4" />
            Invite a Parent
            {pendingInvites && pendingInvites.length > 0 && (
              <span className="bg-muted text-muted-foreground text-xs px-1.5 py-0.5 rounded-full">
                {pendingInvites.length} pending
              </span>
            )}
          </span>
          {invitePanelOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>

        {invitePanelOpen && (
          <div className="px-4 pb-4 space-y-4 border-t border-border">
            <p className="text-sm text-muted-foreground pt-3">
              Send an invite link to a parent — they'll create an account and add their child's details.
            </p>

            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Parent email address"
                value={inviteEmail}
                onChange={(e) => { setInviteEmail(e.target.value); setInviteSent(null); }}
                className="flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleSendInvite()}
              />
              <Button onClick={handleSendInvite} disabled={inviteSending || !inviteEmail.trim()} className="shrink-0">
                {inviteSending ? 'Sending…' : 'Send Invite'}
              </Button>
            </div>

            {inviteSent && (
              <div className="bg-card border border-border rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-foreground">Invite sent!</p>
                <div className="flex items-center gap-2">
                  <code className="text-base font-bold tracking-widest text-primary bg-muted px-3 py-1 rounded border border-border">
                    {inviteSent}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => copyOnboardLink(inviteSent)} className="flex items-center gap-1">
                    <Copy className="w-3 h-3" />
                    {copiedCode === inviteSent ? 'Copied!' : 'Copy link'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Share the link manually if email is not configured.</p>
              </div>
            )}

            {pendingInvites && pendingInvites.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pending invites</p>
                {pendingInvites.map((invite) => (
                  <div key={invite.id} className="flex items-center justify-between bg-card rounded-lg px-3 py-2 border border-border">
                    <div className="flex items-center gap-3 min-w-0">
                      <code className="font-mono text-xs font-bold tracking-wider text-primary shrink-0">{invite.code}</code>
                      <span className="text-sm text-muted-foreground truncate">{invite.parentEmail}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => copyOnboardLink(invite.code)} className="h-7 px-2">
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => revokeInvite({ inviteId: invite.id })} className="h-7 px-2 text-red-400 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search and Filter Bar */}
      <PlayerFilters
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        levelFilter={levelFilter}
        onLevelFilterChange={setLevelFilter}
        ballLevelFilter={ballLevelFilter}
        onBallLevelFilterChange={setBallLevelFilter}
        onAddPlayer={handleAddPlayer}
      />

      {/* Player List */}
      <PlayerTable
        players={data?.players ?? []}
        isLoading={isLoading}
        total={data?.total ?? 0}
        emptyMessage={
          debouncedSearch || levelFilter
            ? 'No players match your search'
            : 'No players in your club yet. Add your first player!'
        }
        onViewPlayer={(id) => navigate(`/director/players/${id}`)}
        onEditPlayer={handleEditPlayer}
        onDeletePlayer={setDeleteConfirmPlayer}
      />

      {/* Add/Edit Modal */}
      <PlayerFormModal
        open={formModalOpen}
        onOpenChange={setFormModalOpen}
        player={editingPlayer}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteConfirmPlayer}
        onOpenChange={(open) => !open && setDeleteConfirmPlayer(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Player</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteConfirmPlayer?.firstName} {deleteConfirmPlayer?.lastName}?
              This will also remove all their attendance records, evaluations, and coach assignments.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
